import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Column, Row } from '../../models/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

@Component({
  standalone: true,
  template: '',
})
export abstract class TableAbstractComponent<T extends Row>{
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() public columns: Column[];
  @Input() public editable: boolean;
  @Input() public pageable: boolean;
  @Input() public hiddenColumns: string[];
  @Input() public expandedItemId: string;
  @Input() public last: boolean;
  @Input() public selection: SelectionModel<T>;
  @Input() public hover: boolean;
  @Input() public isExpandable: boolean;
  @Input() public isSelectable: boolean;
  @Input() public multiselectDisabled: boolean;
  @Input() public isFloatingMenu: boolean;
  @Input() protected expandedElement: T | null;
  @Input() validation?: (row: T) => boolean;
  protected editedRow: T | null = null;
  protected changingRow: T | null = null;

  @Input() expandedComponent: any;

  private hoverElements: Record<string, boolean> = {};
  dataSource: MatTableDataSource<T>;
  private _data: T[];
  @Input() set data(value: T[]) {
    this._data = value;
    this.dataSource = new MatTableDataSource<T>(this._data);
  }


  @Output() public addedSelection = new EventEmitter<any[]>();
  @Output() public removedSelection = new EventEmitter<any[]>();

  @Output() public rowOnclick = new EventEmitter<any>();
  @Output() public dataChangedEvent = new EventEmitter<{ value: T, success: boolean }>();
  @Output() public pageEvent = new EventEmitter<PageEvent>();




  public hoverElement(element: any) {
    this.hoverElements[element.id] = true;
  }

  public unhoverElement(element: any) {
    this.hoverElements[element.id] = false;
  }

  public isElementHovered(element: any) {
    return !!this.hoverElements[element.id];
  }

  public isAllSelected() {
    if (this._data.length === 0) {
      return false;
    }
    if (this._data.filter(value => !value.isDisabled).length === 0) {
      return false;
    }
    return this._data.filter(value => !value.isDisabled && !this.isSelected(value)).length === 0;
  }

  get hasSelectedValue() {
    return this._data.filter(value => this.isSelected(value)).length > 0;
  }

  get hasSelectedAnotherValue() {
    return this.selection.selected.filter(value => !this._data.includes(value)).length > 0;
  }

  public onToggleAll() {
    if (this.multiselectDisabled) {
      return;
    }
    this.isAllSelected() ? this.deselectAll() : this.selectAll();
  }

  get isSelectAllDisabled() {
    return this._data.filter(value => !value.isDisabled).length === 0;
  }

  public deselectAll() {
    let ids = this._data.map(value => value.id);
    this.selection.deselect(...this.selection.selected.filter(value => !value.isDisabled && ids.includes(value.id)));
  }

  private selectAll() {
    const notDisabled = this._data.filter(row => !row.isDisabled &&
      (this.selection.selected.length === 0 || this.selection.selected.findIndex(value => value.id === row.id) === -1));
    this.selection.select(...notDisabled);
  }

  //todo do poprawy
  checkboxLabel(row?: any): string {
    if (!row) {
      return `${this.isAllSelected() ? 'select' : 'deselect'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.id + 1}`;
  }

  checkboxClick(event: MouseEvent, element: T) {
    event.stopPropagation();
    if (!this.isCheckboxSelectionDisabled(element)) {
      this.toggleElement(element);
    }
  }

  expandableMenuTdClick(event: MouseEvent) {
    event.stopPropagation();
  }

  checkboxMobileClick(event: MouseEvent, element: T) {
    event.stopPropagation();
    if (!this.isCheckboxSelectionDisabled(element)) {
      this.toggleElement(element);
    }
  }

  public isCheckboxSelectionDisabled(element: T): boolean {
    if (element.isDisabled) {
      return true;
    }
    return this.multiselectDisabled && this.selection.selected.length > 0 && !this.isSelected(element);
  }

  public isSelected(element: T) {
    return this.selection.selected.some(value => value.id === element.id);
  }

  toggleElement(element: T) {
    if (this.isCheckboxSelectionDisabled(element) && !this.isSelected(element)) {
      return;
    }
    if (this.isSelected(element)) {
      this.selection.deselect(this.selection.selected.find(value => value.id === element.id));
    } else {
      this.selection.select(element);
    }
  }

  selectElement(element: T) {
    if (!!element && !this.isSelected(element)) {
      if (this.multiselectDisabled && this.selection.selected.length > 0) {
        return;
      }
      this.selection.select(element);
    }
  }

  deselectElement(element: T) {
    if (!!element && this.isSelected(element)) {
      this.selection.deselect(this.selection.selected.find(value => value.id === element.id));
    }
  }

  isEven(element: Row) {
    return this._data.findIndex(value => value === element) % 2 === 1;
  }


  editElement(row: T) {
    this.changingRow = Object.assign({}, row);
    this.editedRow = row;
  }

  saveEditedElement() {
    if (this.editedRow) {
      if (this.validation) {
        if (this.validation(this.changingRow)) {
          this.saveData();
        } else {
          this.dataChangedEvent.emit({ value: this.editedRow, success: false });
        }
      } else {
        this.saveData();
      }
    }
  }

  private saveData() {
    const index = this._data.findIndex(item => item === this.editedRow);
    if (index !== -1) {
      this._data[index] = Object.assign({}, this.changingRow);
      this._data = [...this._data];
      this.dataChangedEvent.emit({ value: this._data[index], success: true });
      this.editedRow = null;
      this.changingRow = null;
    }
    this.dataSource.data = this._data;
  }

  cancelEditing() {

    this.editedRow = null;
    this.changingRow = null;
  }

  checkboxValue(element: any, columnName: string): any {
    return element[columnName];
  }

  checkboxChecked(element: any, columnName: string): any {
    return element[columnName];
  }

  checkboxIndeterminate(element: any, columnName: string): any {
    return element[columnName] === false;
  }

}
